import _ from "lodash";
import { CheckIcon, ExclamationTriangleIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PlusCircleIcon, CheckBadgeIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { IconButton, OvalLoader, DateFormat, Tooltip } from "@avenueops/web-components";
import { useUser } from "context/UserContext";
import { z } from "zod";
import { FieldError, useFormContext, useWatch } from "react-hook-form";
import React, { EventHandler, MouseEvent, useCallback, useState } from "react";
import { PlaybookInputZod, PlaybookStepInputZod } from "@avenueops/shared-types";
import { useReadOnly } from "./ReadOnlyProvider";
import { useEditingBlocksContext } from "./EditingBlocks";

export interface GenericBlockProps {
  id?: string;
  decorator: "TASK" | "CHECK" | "DEFAULT" | "NONE" | "ACTIVE" | "SOLID" | "PLUS" | "QUESTION" | "APPROVAL" | undefined;
  topLineStyle?: "SOLID" | "THICK" | "BLANK";
  bottomLineStyle?: "SOLID" | "THICK" | "BLANK" | "NESTED";
  errors?: string[];
  onDecoratorClick?: EventHandler<MouseEvent>;
  isEditing?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  removeNode?: () => void;
  isComplete?: boolean;
  completedAt?: Date | null;
  nested?: boolean;
  executionError?: string | null;
}

export const GenericBlock = ({
  id,
  children,
  decorator,
  isEditing = false,
  errors = [],
  executionError,
  setIsEditing,
  onDecoratorClick,
  topLineStyle = "SOLID",
  bottomLineStyle = "SOLID",
  isComplete = false,
  completedAt = null,
  removeNode,
  nested = false,
}: React.PropsWithChildren<GenericBlockProps>) => {
  const [hovering, setHovering] = useState(false);
  const user = useUser();
  const completedSuccessfully = isComplete && !executionError;
  const completedWithErrors = isComplete && executionError;
  let decoratorComponent = (
    <div
      className={clsx(
        "border rounded-full overflow-show",
        onDecoratorClick ? "cursor-pointer" : "cursor-default",
        {
          "w-8 h-8": decorator === "PLUS",
          "w-4 h-4": decorator === "TASK" || decorator === "CHECK",
          "w-2 h-2": decorator === "DEFAULT" || decorator === undefined, // All other decorators (for now just undefined)
          "w-1 h-1": decorator === "SOLID",
        },
        {
          "bg-white border-0": decorator === "PLUS" || decorator === "QUESTION" || decorator === "APPROVAL",
          "border-primary-main bg-primary-main": completedSuccessfully,
          "border-red-600 bg-red-600": completedWithErrors,
          "border-gray-300 bg-white": !isComplete && (decorator === "TASK" || decorator === "DEFAULT" || decorator === "ACTIVE"),
          "bg-gray-300": decorator === "SOLID",
          "border-green-500 bg-green-500": !isComplete && decorator === "CHECK",
        },
      )}
      onClick={onDecoratorClick}
    >
      {decorator === "PLUS" ? (
        <div className="h-full w-full flex justify-center items-center">
          <PlusCircleIcon className="w-8 h-8 text-primary-main" />
        </div>
      ) : decorator === "QUESTION" ? (
        <div className="h-full w-full flex justify-items-stretch items-center">
          <QuestionMarkCircleIcon strokeWidth={1} className={`w-5 h-5 ${completedSuccessfully ? "text-white" : "text-gray-300"}`} />
        </div>
      ) : decorator === "APPROVAL" ? (
        <div className="h-full w-full flex justify-items-stretch items-center">
          <CheckBadgeIcon strokeWidth={1.5} className={`w-5 h-5 ${completedSuccessfully ? "text-primary-main" : "text-gray-300"}`} />
        </div>
      ) : decorator === "CHECK" || (decorator === "TASK" && completedSuccessfully) ? (
        <div className="h-full w-full flex justify-center items-center">
          <CheckIcon strokeWidth={6} className="w-2 h-2 text-white" />
        </div>
      ) : decorator === "ACTIVE" ? (
        <div className="h-full w-full flex justify-center items-center">
          <OvalLoader size={10} strokeWidth={10} />
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className={`flex `}
      onMouseEnter={(e) => {
        setHovering(true);
      }}
      onMouseLeave={(e) => {
        setHovering(false);
      }}
    >
      <div className={`relative pl-8 w-full h-full flex justify-items-stretch ${isComplete ? "bg-gray-50 bg-opacity-70" : ""} `}>
        <div className="flex flex-col w-3 items-center relative shrink-0">
          <div
            className={clsx(
              {
                "bg-gray-300": !isComplete,
                "bg-primary-main": completedSuccessfully,
                "bg-red-600": completedWithErrors,
              },
              {
                "w-[1px]": topLineStyle === "SOLID",
                "w-2 rounded-t": topLineStyle === "THICK",
              },
              {
                "flex-auto": topLineStyle !== "BLANK",
              },
            )}
          ></div>
          <div className={clsx("absolute", { "top-1/2 transform -translate-y-1/2": topLineStyle !== "BLANK" })}>{decoratorComponent}</div>
          <div
            className={clsx("flex-auto", {
              "bg-gray-300": !isComplete,
              "bg-primary-main": completedSuccessfully,
              "bg-red-600": completedWithErrors,
              "w-[1px]": bottomLineStyle === "SOLID",
              "w-2 rounded-b": bottomLineStyle === "THICK",
              "w-0": bottomLineStyle === "BLANK",
              "rounded-bl-full h-2 self-start ml-1.5 bg-transparent border-dashed border-gray-300 border-b-[1px] border-l w-[39px]":
                bottomLineStyle === "NESTED",
              "border-primary-main": bottomLineStyle === "NESTED" && isComplete,
            })}
          ></div>
        </div>
        <div
          className={clsx(
            "my-1 w-full text-gray-950 p-2 relative rounded mr-1",

            {
              "border-primary-main border-dotted border-2": isEditing && errors.length === 0,
              "rounded ring-1 ring-primary-main": hovering && !isEditing && setIsEditing,
              "border-red-600 border-2": isEditing && errors.length > 0,
            },
            { "pt-0": topLineStyle === "BLANK" },
          )}
        >
          {errors.length > 0 ? (
            <div className="absolute -top-1 -right-1 bg-white">
              <Tooltip tooltip={errors.join("\n")}>
                <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
              </Tooltip>
            </div>
          ) : null}
          {executionError ? (
            <div className="flex gap-1 items-center">
              <p className="font-medium text-sm text-red-600">Skipped</p>
              <Tooltip tooltip={executionError}>
                <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
              </Tooltip>
            </div>
          ) : null}

          {children}
          {isComplete && completedAt ? (
            <span className="text-xs text-gray-500">
              <DateFormat type="ABSOLUTE" date={completedAt} timeZone={user.timeZone ?? undefined} />
            </span>
          ) : null}
        </div>
      </div>
      {setIsEditing ? (
        <div className={clsx("flex h-fit mt-1 gap-2 justify-start shadow-sm rounded-md", hovering ? "visible" : "invisible")}>
          {!isEditing ? (
            <IconButton onClick={() => setIsEditing?.(!isEditing)} Icon={PencilIcon} size="SMALL" />
          ) : (
            <IconButton onClick={() => setIsEditing?.(!isEditing)} Icon={CheckIcon} size="SMALL" type="SUCCESS" />
          )}

          {removeNode ? <IconButton onClick={removeNode} Icon={TrashIcon} size="SMALL" type="DANGER" /> : null}
        </div>
      ) : null}
    </div>
  );
};

const validateStep =
  (zodParser: z.ZodType<any, any>, triggerFormValidation: () => Promise<any>) =>
  (step: any): boolean => {
    const result = zodParser.safeParse(step);
    if (!result.success) {
      console.error(result.error);
      triggerFormValidation();
      return false;
    }
    return true;
  };

export type EditablePlaybookBlockProps<StepType> = {
  id: string;
  index?: number;
  stepPrefix: `steps.${number}`;
  zodValidator: z.ZodType<any, any>;
  columns: string[];
  ViewComponent: React.FC<{ step: StepType; index?: number }>;
  EditComponent: React.FC<{ stepPrefix: any; columns: string[]; index?: number }>;
  removeNode: () => void;
} & Omit<GenericBlockProps, "isEditing" | "setIsEditing" | "removeNode">;

export function EditablePlaybookBlock<StepType = PlaybookStepInputZod>({
  id,
  stepPrefix,
  zodValidator,
  columns,
  ViewComponent,
  EditComponent,
  children,
  index,
  ...props
}: React.PropsWithChildren<EditablePlaybookBlockProps<StepType>>) {
  const { readOnly } = useReadOnly();
  const { editingBlocks, setEditingBlocks } = useEditingBlocksContext();
  const { trigger, getFieldState } = useFormContext<PlaybookInputZod>();
  const step = useWatch({ name: stepPrefix });
  const { error: fieldErrors } = getFieldState<`steps.${number}`>(stepPrefix);
  const errors = fieldErrors as unknown as Record<string, FieldError>;
  /**
   * Converting the errors object to a list of strings. FieldError object can look like (the inferred type above is not accurate)
   * {
   *  "FIELD_NAME": {
   *    message: "STRING"
   *  }
   * }
   *
   * Nested objects are not supported in the current implementation (i.e getting the error of a step inside an if/else).
   * However, the nested block should recieve its 'own' error message.
   */
  const errorMessages = errors
    ? Object.keys(errors).reduce((acc, key) => {
        if (errors[key]?.message) {
          return [...acc, errors[key].message!];
        }
        return acc;
      }, [] as string[])
    : [];
  const isEditing = (!readOnly && editingBlocks[id]) ?? false;
  const taskBlockValidator = validateStep(zodValidator, () => trigger(stepPrefix));
  return (
    <GenericBlock
      {...props}
      id={id}
      errors={errorMessages}
      isEditing={isEditing}
      setIsEditing={
        !readOnly
          ? (newEditState) => {
              if (!newEditState) {
                const validationResult = taskBlockValidator(step);
                if (!validationResult) {
                  return;
                }
              }
              setEditingBlocks(id, newEditState);
            }
          : undefined
      }
    >
      {!isEditing ? (
        <div className="pl-2">
          <ViewComponent step={step} index={index} />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="font-medium text-primary-main">{_.startCase(_.toLower(step.type))}</span>
          <EditComponent stepPrefix={stepPrefix} columns={columns} index={index} />
        </div>
      )}
      {children}
    </GenericBlock>
  );
}

export const EndBlock = ({ nested = false, isComplete = false, children }: React.PropsWithChildren<{ nested?: boolean; isComplete?: boolean }>) => {
  return nested ? (
    <GenericBlock decorator="SOLID" nested={nested} bottomLineStyle="BLANK" isComplete={isComplete}>
      <div>{children}</div>
    </GenericBlock>
  ) : (
    <GenericBlock decorator="CHECK" bottomLineStyle="BLANK">
      <div className="p-2">{children}</div>
    </GenericBlock>
  );
};
